import { CartAddEvent } from '@theme/events';

const ROOT = window.Shopify?.routes?.root || '/';
const CART_URL = `${ROOT}cart.js`;
const CART_ADD_URL = `${ROOT}cart/add.js`;
const TRACKING_VERSION = 'aov-v1';

async function fetchCart() {
  const response = await fetch(CART_URL, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
  return response.json();
}

function emitAovEvent(action, detail = {}) {
  const payload = {
    action,
    source: 'smart_bundle',
    context: 'product',
    bundleType: detail.bundleType || '',
    anchorProductId: String(detail.anchorProductId || ''),
    offerProductId: String(detail.offerProductId || ''),
    variantIds: Array.isArray(detail.variantIds) ? detail.variantIds.map(String) : [],
    version: TRACKING_VERSION,
  };

  window.dispatchEvent(new CustomEvent('isarttools:aov', { detail: payload }));

  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event: `isarttools_aov_${action}`, ...payload });
  }

  if (typeof window.fbq === 'function') {
    try {
      window.fbq('trackCustom', action === 'add' ? 'IsarttoolsAOVAdd' : 'IsarttoolsAOVClick', payload);
    } catch (_) {}
  }
}

class IsarttoolsSmartBundle extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.mountNearBuyButtons();
    this.load();
  }

  mountNearBuyButtons() {
    const buyButtons = document.querySelector('.product-information .buy-buttons-block');
    if (!buyButtons) return;
    const aov = document.querySelector('isarttools-aov-upsell[data-context="product"]');
    if (aov && aov.parentElement === buyButtons.parentElement) aov.after(this);
    else buyButtons.after(this);
  }

  trackingProperties() {
    return {
      _isarttools_source: 'smart_bundle',
      _isarttools_version: TRACKING_VERSION,
      _isarttools_anchor_product: String(this.dataset.productId || ''),
      _isarttools_bundle_type: String(this.dataset.bundleType || ''),
    };
  }

  attributedUrl(url) {
    try {
      const target = new URL(url, window.location.origin);
      target.searchParams.set('iast_source', 'smart_bundle');
      target.searchParams.set('iast_anchor', String(this.dataset.productId || ''));
      target.searchParams.set('iast_bundle', String(this.dataset.bundleType || ''));
      target.searchParams.set('iast_version', TRACKING_VERSION);
      return target.toString();
    } catch (_) {
      return url;
    }
  }

  eventDetail(offerProductId = '', variantIds = []) {
    return {
      bundleType: this.dataset.bundleType || '',
      anchorProductId: this.dataset.productId || '',
      offerProductId,
      variantIds,
    };
  }

  async load() {
    const currentProductId = Number(this.dataset.productId || 0);
    const sources = String(this.dataset.sources || '').split('|').map((item) => item.trim()).filter(Boolean);
    if (!currentProductId || !sources.length) {
      this.hidden = true;
      return;
    }

    try {
      const [cart, pools] = await Promise.all([fetchCart(), Promise.all(sources.map((handle) => this.fetchCollection(handle)))]);
      const inCart = new Set((cart.items || []).map((item) => Number(item.product_id)));
      const candidates = this.pickCandidates(pools, inCart, currentProductId);
      this.render(candidates.slice(0, 2));
    } catch (error) {
      console.warn('[Isarttools Smart Bundle] unavailable', error);
      this.hidden = true;
    }
  }

  async fetchCollection(handle) {
    const response = await fetch(`${ROOT}collections/${encodeURIComponent(handle)}/products.json?limit=12`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data.products) ? data.products : [];
  }

  pickCandidates(pools, inCart, currentId) {
    const chosen = [];
    const used = new Set();
    const eligiblePools = pools.map((pool) => pool.filter((product) => this.isEligible(product, inCart, currentId)));

    for (const pool of eligiblePools) {
      const preferred = pool.find((product) => !this.hasChoice(product)) || pool[0];
      if (preferred && !used.has(Number(preferred.id))) {
        chosen.push(preferred);
        used.add(Number(preferred.id));
      }
      if (chosen.length >= 2) return chosen;
    }

    for (const pool of eligiblePools) {
      for (const product of pool) {
        const id = Number(product.id);
        if (used.has(id)) continue;
        chosen.push(product);
        used.add(id);
        if (chosen.length >= 2) return chosen;
      }
    }
    return chosen;
  }

  isEligible(product, inCart, currentId) {
    if (!product) return false;
    const id = Number(product.id);
    if (!id || id === currentId || inCart.has(id)) return false;
    const available = (product.variants || []).filter((variant) => variant.available !== false);
    if (!available.length) return false;

    const expectedShipping = this.dataset.shipping === 'true';
    const withShippingInfo = available.filter((variant) => 'requires_shipping' in variant);
    if (withShippingInfo.length && !withShippingInfo.some((variant) => variant.requires_shipping === expectedShipping)) {
      return false;
    }
    return true;
  }

  getVariant(product) {
    const available = (product.variants || []).filter((variant) => variant.available !== false);
    const expectedShipping = this.dataset.shipping === 'true';
    return available.find((variant) => !('requires_shipping' in variant) || variant.requires_shipping === expectedShipping) || available[0] || null;
  }

  hasChoice(product) {
    const available = (product.variants || []).filter((variant) => variant.available !== false);
    if (available.length <= 1) return false;
    return (product.options || []).some((option) => String(option?.name || option).toLowerCase() !== 'title');
  }

  render(products) {
    const container = this.querySelector('[data-smart-products]');
    const footer = this.querySelector('[data-smart-footer]');
    if (!container || !footer || !products.length) {
      this.hidden = true;
      return;
    }

    container.replaceChildren(...products.map((product) => this.createCard(product)));
    this.renderFooter(products, footer);
    this.hidden = false;
  }

  createCard(product) {
    const card = document.createElement('div');
    card.className = 'isarttools-smart-bundle__item';
    const rawUrl = product.url || `${ROOT}products/${product.handle}`;
    const url = this.attributedUrl(rawUrl);

    const imageLink = document.createElement('a');
    imageLink.className = 'isarttools-smart-bundle__image-link';
    imageLink.href = url;
    imageLink.addEventListener('click', () => emitAovEvent('click', this.eventDetail(product.id)));
    const imageSource = product.featured_image || product.images?.[0]?.src || product.images?.[0];
    if (imageSource) {
      const image = document.createElement('img');
      image.className = 'isarttools-smart-bundle__image';
      image.src = typeof imageSource === 'string' ? imageSource : imageSource.src;
      image.alt = product.title || '';
      image.loading = 'lazy';
      image.width = 116;
      image.height = 116;
      imageLink.append(image);
    }

    const info = document.createElement('div');
    info.className = 'isarttools-smart-bundle__info';
    const name = document.createElement('a');
    name.className = 'isarttools-smart-bundle__name';
    name.href = url;
    name.textContent = product.title || '';
    name.addEventListener('click', () => emitAovEvent('click', this.eventDetail(product.id)));
    const variant = this.getVariant(product);
    const price = document.createElement('span');
    price.className = 'isarttools-smart-bundle__price';
    price.textContent = this.formatMoney(this.priceToCents(variant?.price ?? product.price ?? 0));
    info.append(name, price);

    let action;
    if (!variant || this.hasChoice(product)) {
      action = document.createElement('a');
      action.href = url;
      action.textContent = 'اختيار';
      action.addEventListener('click', () => emitAovEvent('click', this.eventDetail(product.id)));
    } else {
      action = document.createElement('button');
      action.type = 'button';
      action.textContent = '+ إضافة';
      action.addEventListener('click', () => this.addVariants(action, [variant], 'تمت الإضافة ✓', product.id));
    }
    action.className = 'isarttools-smart-bundle__action';
    card.append(imageLink, info, action);
    return card;
  }

  renderFooter(products, footer) {
    footer.replaceChildren();
    const variants = products.map((product) => (this.hasChoice(product) ? null : this.getVariant(product)));
    if (products.length >= 2 && variants.every(Boolean)) {
      const total = variants.reduce((sum, variant) => sum + this.priceToCents(variant.price), 0);
      const row = document.createElement('div');
      row.className = 'isarttools-smart-bundle__total';
      const label = document.createElement('span');
      label.textContent = 'الإضافات المقترحة';
      const value = document.createElement('strong');
      value.textContent = this.formatMoney(total);
      row.append(label, value);

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'isarttools-smart-bundle__add-all';
      button.textContent = 'أضف الاختيارين للسلة';
      button.addEventListener('click', () => this.addVariants(button, variants, 'تمت إضافة الباكدج ✓', 'bundle-pair'));
      footer.append(row, button);
      footer.hidden = false;
      return;
    }

    const note = document.createElement('p');
    note.className = 'isarttools-smart-bundle__choice-note';
    note.textContent = 'اختار المقاس أو النوع المناسب لكل إضافة قبل الشراء.';
    footer.append(note);
    footer.hidden = false;
  }

  priceToCents(value) {
    if (typeof value === 'number') return Math.round(value * 100);
    const parsed = Number.parseFloat(String(value || '0').replace(/,/g, ''));
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
  }

  formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'EGP';
    const locale = document.documentElement.lang || 'ar-EG';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 2 }).format(cents / 100);
    } catch (_) {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  }

  async addVariants(button, variants, successText, offerProductId = '') {
    const properties = this.trackingProperties();
    const items = variants
      .filter(Boolean)
      .map((variant) => ({ id: Number(variant.id), quantity: 1, properties }))
      .filter((item) => item.id);
    if (!items.length || button.disabled) return;

    emitAovEvent('click', this.eventDetail(offerProductId, items.map((item) => item.id)));
    button.disabled = true;
    const original = button.textContent;
    button.textContent = 'جارٍ الإضافة…';

    try {
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((element) => element.dataset.sectionId)
        .filter(Boolean);
      const response = await fetch(CART_ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const detail = await response.json();
      if (!response.ok || detail.status) throw new Error(detail.description || 'Unable to add bundle items');

      const cart = await fetchCart();
      emitAovEvent('add', this.eventDetail(offerProductId, items.map((item) => item.id)));
      this.dispatchEvent(new CartAddEvent(cart, 'isarttools-smart-bundle', {
        source: 'isarttools-smart-bundle',
        itemCount: cart.item_count,
        sections: detail.sections || {},
      }));
      button.textContent = successText;
      window.setTimeout(() => this.load(), 450);
    } catch (error) {
      console.warn('[Isarttools Smart Bundle] add failed', error);
      button.textContent = 'تعذر الإضافة';
      window.setTimeout(() => {
        if (button.isConnected) {
          button.disabled = false;
          button.textContent = original;
        }
      }, 1300);
      return;
    }

    window.setTimeout(() => {
      if (button.isConnected) button.disabled = false;
    }, 550);
  }
}

if (!customElements.get('isarttools-smart-bundle')) {
  customElements.define('isarttools-smart-bundle', IsarttoolsSmartBundle);
}
